import type React from 'react';

import { Button } from '@change/ds-core-ui';
import { ShareIcon } from '@change/ds-icons';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { UseMobileNativeShareReturn } from '#src/app/pages/petitionDetails/shared/hooks';

type Props = {
	placement: 'below' | 'beside';
	shareAction: UseMobileNativeShareReturn;
};

/**
 * Fires the same OS native share sheet as the hero-photo `NativeShareMobile` FAB — no modal,
 * no `useMobileModalContext`, no `ShareEducationModalProvider`, since the native-share path needs
 * none of them. `shareAction` is resolved once by the caller (which also uses it to gate
 * `$exposure`) and passed down here, rather than re-derived via a second `useMobileNativeShare`
 * call with the same tracking context.
 */
export const SignCtaShareAffordance: React.FC<Props> = ({ placement, shareAction }) => {
	const { translate } = useI18n();

	// Render nothing when the resolved share action is unavailable — never a dead button.
	if (!shareAction) {
		return null;
	}

	const { ShareAction } = shareAction;
	const commonProps = {
		leadingIcon: <ShareIcon />,
		variant: 'secondary' as const,
		size: 'large' as const,
		'data-qa': 'sign-cta-share-affordance',
		'data-testid': 'sign-cta-share-affordance',
	};

	// `iconOnly` is a discriminated union on `Button` (its `aria-label` requirement flips with it),
	// so it can't be spread as a plain boolean — hence two branches rather than one ternary prop.
	return (
		<ShareAction>
			{placement === 'beside' ? (
				<Button
					{...commonProps}
					iconOnly
					aria-label={translate('fe.components.petition_share.action')}
					className="min-h-[44px] min-w-[44px] shrink-0"
				/>
			) : (
				<Button {...commonProps} className="w-full max-w-none">
					<Translate value="fe.components.petition_share.action" />
				</Button>
			)}
		</ShareAction>
	);
};
